import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";

import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./DefinitionMatching.css";

const DEFINITIONS_BANK = [
  {
    id: "shelf",
    category: "Casa",
    definition:
      "Mueble formado por estantes en el que suelen colocarse libros y objetos decorativos.",
    answer: "Estantería",
    distractors: ["Mesa", "Silla", "Sofá", "Cama"],
    difficulty: "easy",
  },
  {
    id: "frying_pan",
    category: "Cocina",
    definition:
      "Utensilio de cocina redondo, poco profundo y con un mango largo, utilizado especialmente para freír.",
    answer: "Sartén",
    distractors: ["Taza", "Cazuela", "Plato", "Cuchara"],
    difficulty: "easy",
  },
  {
    id: "wastebasket",
    category: "Objetos",
    definition:
      "Recipiente utilizado para tirar papeles y otros objetos que ya no sirven.",
    answer: "Papelera",
    distractors: ["Canasta", "Cesto", "Mochila", "Maceta"],
    difficulty: "easy",
  },
  {
    id: "carpet",
    category: "Casa",
    definition:
      "Tejido grueso que cubre parte del suelo para decorar una habitación o proteger del frío.",
    answer: "Alfombra",
    distractors: ["Toalla", "Felpudo", "Manta", "Cortina"],
    difficulty: "easy",
  },
  {
    id: "football",
    category: "Deportes",
    definition:
      "Deporte en el que dos equipos intentan introducir un balón en la portería contraria usando principalmente los pies.",
    answer: "Fútbol",
    distractors: ["Tenis", "Baloncesto", "Natación", "Voleibol"],
    difficulty: "easy",
  },
  {
    id: "pharmacy",
    category: "Lugares",
    definition:
      "Establecimiento en el que se preparan, guardan y venden medicamentos.",
    answer: "Farmacia",
    distractors: ["Supermercado", "Mercería", "Óptica", "Panadería"],
    difficulty: "easy",
  },
  {
    id: "fruit_shop",
    category: "Lugares",
    definition:
      "Establecimiento donde se venden principalmente frutas y verduras.",
    answer: "Frutería",
    distractors: ["Farmacia", "Óptica", "Ferretería", "Carnicería"],
    difficulty: "easy",
  },
  {
    id: "pen",
    category: "Material escolar",
    definition:
      "Instrumento que contiene tinta y se utiliza para escribir sobre papel.",
    answer: "Bolígrafo",
    distractors: ["Lapicero", "Portaminas", "Goma", "Regla"],
    difficulty: "easy",
  },
  {
    id: "telephone",
    category: "Comunicación",
    definition:
      "Aparato que permite hablar con otra persona que se encuentra a distancia.",
    answer: "Teléfono",
    distractors: ["Avión", "Televisión", "Radio", "Ordenador"],
    difficulty: "easy",
  },
  {
    id: "bell_tower",
    category: "Edificios",
    definition:
      "Torre alta situada normalmente junto a una iglesia y que contiene una o varias campanas.",
    answer: "Campanario",
    distractors: ["Iglesia", "Catedral", "Castillo", "Ayuntamiento"],
    difficulty: "medium",
  },
  {
    id: "road",
    category: "Transportes",
    definition:
      "Vía preparada para que circulen coches, motocicletas, camiones y otros vehículos.",
    answer: "Carretera",
    distractors: ["Vía", "Ferrocarril", "Acera", "Andén"],
    difficulty: "easy",
  },
  {
    id: "socks",
    category: "Ropa",
    definition:
      "Prenda de vestir que cubre los pies y se utiliza normalmente dentro del calzado.",
    answer: "Calcetines",
    distractors: ["Pantalones", "Guantes", "Bufanda", "Camiseta"],
    difficulty: "easy",
  },
  {
    id: "clock",
    category: "Objetos",
    definition:
      "Objeto que permite conocer la hora mediante agujas o números.",
    answer: "Reloj",
    distractors: ["Radio", "Televisión", "Brújula", "Termómetro"],
    difficulty: "easy",
  },
  {
    id: "truck_driver",
    category: "Profesiones",
    definition:
      "Persona cuyo trabajo consiste en conducir un camión para transportar mercancías.",
    answer: "Camionero",
    distractors: ["Taxista", "Albañil", "Mecánico", "Cartero"],
    difficulty: "easy",
  },
  {
    id: "spoon",
    category: "Cocina",
    definition:
      "Utensilio compuesto por un mango y una parte cóncava que se utiliza para tomar alimentos líquidos.",
    answer: "Cuchara",
    distractors: ["Tenedor", "Cuchillo", "Espátula", "Plato"],
    difficulty: "easy",
  },
  {
    id: "umbrella",
    category: "Objetos",
    definition:
      "Objeto plegable formado por una tela sujeta a varias varillas y utilizado para protegerse de la lluvia.",
    answer: "Paraguas",
    distractors: ["Sombrilla", "Abrigo", "Chubasquero", "Bufanda"],
    difficulty: "easy",
  },
  {
    id: "refrigerator",
    category: "Electrodomésticos",
    definition:
      "Electrodoméstico que mantiene los alimentos y bebidas a baja temperatura para conservarlos.",
    answer: "Nevera",
    distractors: ["Lavadora", "Horno", "Microondas", "Lavavajillas"],
    difficulty: "easy",
  },
  {
    id: "washing_machine",
    category: "Electrodomésticos",
    definition:
      "Electrodoméstico utilizado para lavar la ropa de forma automática.",
    answer: "Lavadora",
    distractors: ["Nevera", "Secadora", "Lavavajillas", "Aspiradora"],
    difficulty: "easy",
  },
  {
    id: "thermometer",
    category: "Salud",
    definition:
      "Instrumento utilizado para medir la temperatura de una persona o de un objeto.",
    answer: "Termómetro",
    distractors: ["Reloj", "Balanza", "Regla", "Brújula"],
    difficulty: "easy",
  },
  {
    id: "stethoscope",
    category: "Salud",
    definition:
      "Instrumento que utilizan los profesionales sanitarios para escuchar los sonidos del corazón y los pulmones.",
    answer: "Fonendoscopio",
    distractors: ["Termómetro", "Microscopio", "Telescopio", "Audífono"],
    difficulty: "medium",
  },
  {
    id: "library",
    category: "Lugares",
    definition:
      "Lugar donde se guardan, organizan y prestan libros para su lectura o consulta.",
    answer: "Biblioteca",
    distractors: ["Librería", "Papelería", "Colegio", "Museo"],
    difficulty: "easy",
  },
  {
    id: "bookshop",
    category: "Lugares",
    definition:
      "Establecimiento dedicado a la venta de libros.",
    answer: "Librería",
    distractors: ["Biblioteca", "Papelería", "Imprenta", "Museo"],
    difficulty: "medium",
  },
  {
    id: "bakery",
    category: "Lugares",
    definition:
      "Establecimiento donde se fabrica o vende pan y otros productos elaborados con harina.",
    answer: "Panadería",
    distractors: ["Pastelería", "Carnicería", "Frutería", "Pescadería"],
    difficulty: "easy",
  },
  {
    id: "butcher_shop",
    category: "Lugares",
    definition:
      "Establecimiento especializado en la venta de carne.",
    answer: "Carnicería",
    distractors: ["Pescadería", "Charcutería", "Panadería", "Frutería"],
    difficulty: "easy",
  },
  {
    id: "fish_shop",
    category: "Lugares",
    definition:
      "Establecimiento especializado en la venta de pescado y marisco.",
    answer: "Pescadería",
    distractors: ["Carnicería", "Frutería", "Panadería", "Ferretería"],
    difficulty: "easy",
  },
  {
    id: "optician",
    category: "Profesiones",
    definition:
      "Profesional que prepara, adapta y vende gafas y lentes para corregir problemas de visión.",
    answer: "Óptico",
    distractors: ["Dentista", "Farmacéutico", "Oculista", "Veterinario"],
    difficulty: "medium",
  },
  {
    id: "dentist",
    category: "Profesiones",
    definition:
      "Profesional sanitario especializado en el cuidado y tratamiento de los dientes.",
    answer: "Dentista",
    distractors: ["Médico", "Óptico", "Veterinario", "Farmacéutico"],
    difficulty: "easy",
  },
  {
    id: "veterinarian",
    category: "Profesiones",
    definition:
      "Profesional sanitario que se ocupa de prevenir y tratar las enfermedades de los animales.",
    answer: "Veterinario",
    distractors: ["Dentista", "Médico", "Biólogo", "Granjero"],
    difficulty: "easy",
  },
  {
    id: "architect",
    category: "Profesiones",
    definition:
      "Profesional que diseña edificios y supervisa su construcción.",
    answer: "Arquitecto",
    distractors: ["Albañil", "Carpintero", "Electricista", "Fontanero"],
    difficulty: "medium",
  },
  {
    id: "carpenter",
    category: "Profesiones",
    definition:
      "Persona que trabaja la madera para fabricar o reparar muebles y otros objetos.",
    answer: "Carpintero",
    distractors: ["Fontanero", "Albañil", "Pintor", "Electricista"],
    difficulty: "medium",
  },
  {
    id: "hammer",
    category: "Herramientas",
    definition:
      "Herramienta formada por un mango y una cabeza pesada, utilizada principalmente para golpear clavos.",
    answer: "Martillo",
    distractors: ["Destornillador", "Sierra", "Alicates", "Taladro"],
    difficulty: "easy",
  },
  {
    id: "screwdriver",
    category: "Herramientas",
    definition:
      "Herramienta utilizada para apretar o aflojar tornillos.",
    answer: "Destornillador",
    distractors: ["Martillo", "Alicates", "Sierra", "Llave inglesa"],
    difficulty: "easy",
  },
  {
    id: "saw",
    category: "Herramientas",
    definition:
      "Herramienta con una hoja dentada que se utiliza para cortar madera u otros materiales.",
    answer: "Sierra",
    distractors: ["Martillo", "Taladro", "Alicates", "Lima"],
    difficulty: "easy",
  },
  {
    id: "compass",
    category: "Instrumentos",
    definition:
      "Instrumento que posee una aguja imantada y permite orientarse indicando el norte.",
    answer: "Brújula",
    distractors: ["Reloj", "Termómetro", "Balanza", "Regla"],
    difficulty: "medium",
  },
  {
    id: "microscope",
    category: "Instrumentos",
    definition:
      "Instrumento que permite observar objetos o seres demasiado pequeños para verse a simple vista.",
    answer: "Microscopio",
    distractors: ["Telescopio", "Prismáticos", "Fonendoscopio", "Proyector"],
    difficulty: "medium",
  },
  {
    id: "telescope",
    category: "Instrumentos",
    definition:
      "Instrumento que permite observar objetos muy lejanos, especialmente los astros.",
    answer: "Telescopio",
    distractors: ["Microscopio", "Prismáticos", "Periscopio", "Proyector"],
    difficulty: "medium",
  },
  {
    id: "dictionary",
    category: "Lenguaje",
    definition:
      "Libro o recurso que recoge palabras de una lengua y explica su significado.",
    answer: "Diccionario",
    distractors: ["Enciclopedia", "Novela", "Atlas", "Cuaderno"],
    difficulty: "medium",
  },
  {
    id: "atlas",
    category: "Geografía",
    definition:
      "Libro que contiene una colección de mapas geográficos.",
    answer: "Atlas",
    distractors: ["Diccionario", "Enciclopedia", "Agenda", "Novela"],
    difficulty: "medium",
  },
  {
    id: "island",
    category: "Geografía",
    definition:
      "Porción de tierra rodeada completamente de agua.",
    answer: "Isla",
    distractors: ["Península", "Montaña", "Valle", "Desierto"],
    difficulty: "medium",
  },
  {
    id: "peninsula",
    category: "Geografía",
    definition:
      "Extensión de tierra rodeada de agua por casi todos sus lados y unida al continente por una zona estrecha.",
    answer: "Península",
    distractors: ["Isla", "Archipiélago", "Cabo", "Valle"],
    difficulty: "hard",
  },
  {
    id: "archipelago",
    category: "Geografía",
    definition:
      "Conjunto de varias islas próximas entre sí.",
    answer: "Archipiélago",
    distractors: ["Península", "Continente", "Cordillera", "Valle"],
    difficulty: "hard",
  },
  {
    id: "volcano",
    category: "Naturaleza",
    definition:
      "Abertura de la corteza terrestre por la que pueden salir lava, gases y cenizas.",
    answer: "Volcán",
    distractors: ["Montaña", "Cueva", "Valle", "Acantilado"],
    difficulty: "medium",
  },
  {
    id: "earthquake",
    category: "Naturaleza",
    definition:
      "Movimiento brusco de la superficie terrestre producido por fuerzas del interior de la Tierra.",
    answer: "Terremoto",
    distractors: ["Huracán", "Inundación", "Erupción", "Tormenta"],
    difficulty: "hard",
  },
  {
    id: "hurricane",
    category: "Naturaleza",
    definition:
      "Fenómeno atmosférico caracterizado por vientos extremadamente fuertes que giran alrededor de un centro.",
    answer: "Huracán",
    distractors: ["Terremoto", "Tsunami", "Tormenta", "Inundación"],
    difficulty: "hard",
  },
  {
    id: "photosynthesis",
    category: "Ciencias",
    definition:
      "Proceso mediante el cual las plantas utilizan la luz para fabricar su propio alimento.",
    answer: "Fotosíntesis",
    distractors: ["Respiración", "Digestión", "Germinación", "Evaporación"],
    difficulty: "hard",
  },
  {
    id: "evaporation",
    category: "Ciencias",
    definition:
      "Proceso por el que un líquido se transforma en vapor debido al aumento de temperatura.",
    answer: "Evaporación",
    distractors: ["Condensación", "Solidificación", "Congelación", "Precipitación"],
    difficulty: "hard",
  },
  {
    id: "condensation",
    category: "Ciencias",
    definition:
      "Proceso mediante el cual un gas o vapor se transforma en líquido.",
    answer: "Condensación",
    distractors: ["Evaporación", "Fusión", "Solidificación", "Precipitación"],
    difficulty: "hard",
  },
  {
    id: "democracy",
    category: "Sociedad",
    definition:
      "Sistema político en el que la ciudadanía participa en la elección de sus representantes.",
    answer: "Democracia",
    distractors: ["Monarquía", "Dictadura", "República", "Constitución"],
    difficulty: "hard",
  },
  {
    id: "empathy",
    category: "Emociones",
    definition:
      "Capacidad para comprender los sentimientos y puntos de vista de otra persona.",
    answer: "Empatía",
    distractors: ["Simpatía", "Alegría", "Paciencia", "Generosidad"],
    difficulty: "hard",
  },
  {
    id: "patience",
    category: "Emociones",
    definition:
      "Capacidad para esperar o afrontar una dificultad sin perder la calma.",
    answer: "Paciencia",
    distractors: ["Prudencia", "Empatía", "Valentía", "Generosidad"],
    difficulty: "medium",
  },
  {
    id: "generosity",
    category: "Valores",
    definition:
      "Cualidad de quien comparte o ayuda a los demás sin esperar algo a cambio.",
    answer: "Generosidad",
    distractors: ["Sinceridad", "Valentía", "Paciencia", "Responsabilidad"],
    difficulty: "medium",
  },
  {
    id: "responsibility",
    category: "Valores",
    definition:
      "Capacidad de cumplir las obligaciones y asumir las consecuencias de los propios actos.",
    answer: "Responsabilidad",
    distractors: ["Generosidad", "Amabilidad", "Valentía", "Creatividad"],
    difficulty: "hard",
  },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 3,
    allowedDifficulties: ["easy"],
    showCategory: true,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 4,
    allowedDifficulties: ["easy", "medium"],
    showCategory: true,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 5,
    allowedDifficulties: ["medium", "hard"],
    showCategory: false,
  },
};

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const createRound = ({
  itemCount,
  allowedDifficulties,
  previousId = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 3, 2),
    5
  );

  const difficulties =
    Array.isArray(allowedDifficulties) &&
    allowedDifficulties.length > 0
      ? allowedDifficulties
      : ["easy"];

  const filteredDefinitions = DEFINITIONS_BANK.filter(
    (item) =>
      difficulties.includes(item.difficulty) &&
      item.id !== previousId
  );

  const pool =
    filteredDefinitions.length > 0
      ? filteredDefinitions
      : DEFINITIONS_BANK.filter(
          (item) => item.id !== previousId
        );

  const selectedItem =
    pool[Math.floor(Math.random() * pool.length)];

  const distractorCount = safeItemCount - 1;

  const ownDistractors = shuffleArray(
    selectedItem.distractors
  ).slice(0, distractorCount);

  const additionalDistractors = shuffleArray(
    DEFINITIONS_BANK.map((item) => item.answer).filter(
      (word) =>
        word !== selectedItem.answer &&
        !ownDistractors.includes(word)
    )
  );

  const distractors = [
    ...ownDistractors,
    ...additionalDistractors,
  ].slice(0, distractorCount);

  const options = shuffleArray([
    {
      id: `${selectedItem.id}-correct`,
      text: selectedItem.answer,
      isCorrect: true,
    },

    ...distractors.map((text, index) => ({
      id: `${selectedItem.id}-distractor-${index}`,
      text,
      isCorrect: false,
    })),
  ]);

  return {
    id: selectedItem.id,
    category: selectedItem.category,
    definition: selectedItem.definition,
    answer: selectedItem.answer,
    options,
  };
};

function DefinitionMatching() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const [selectedOptionId, setSelectedOptionId] =
    useState(null);

  const [responseLocked, setResponseLocked] =
    useState(false);

  const totalRounds = config?.totalRounds || 0;
  const totalAnswers = correctAnswers + errors;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalAnswers
  );

  const totalErrors = calculateErrorCount(errors);

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig = selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const defaultDifficultyConfig =
      GAME_CONFIG[difficultyKey] || GAME_CONFIG.easy;

    const nextConfig = {
      ...defaultDifficultyConfig,
      ...resolvedConfig,
      difficultyKey,

      totalRounds:
        Number(resolvedConfig.totalRounds) ||
        defaultDifficultyConfig.totalRounds,

      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) ||
            defaultDifficultyConfig.itemCount,
          2
        ),
        5
      ),

      allowedDifficulties:
        resolvedConfig.allowedDifficulties ||
        defaultDifficultyConfig.allowedDifficulties,

      showCategory:
        resolvedConfig.showCategory ??
        defaultDifficultyConfig.showCategory,
    };

    const firstRound = createRound(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setStarted(true);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);

    setSelectedOptionId(null);
    setResponseLocked(false);
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const moveToNextRound = () => {
    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousId: round.id,
    });

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);

    setRoundStartTime(Date.now());

    setSelectedOptionId(null);
    setResponseLocked(false);
  };

  const handleAnswer = (option) => {
    if (!round || responseLocked) {
      return;
    }

    setResponseLocked(true);
    setSelectedOptionId(option.id);

    const responseTime =
      Date.now() - roundStartTime;

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    if (option.isCorrect) {
      setCorrectAnswers(
        (current) => current + 1
      );
    } else {
      setErrors((current) => current + 1);
    }

    window.setTimeout(() => {
      moveToNextRound();
    }, 700);
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Definiciones"
        description="Lee cada definición y selecciona la palabra que mejor corresponde con su significado."
        areas={[
          "Comprensión lectora",
          "Vocabulario",
          "Acceso léxico",
          "Razonamiento verbal",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Opciones de respuesta",
            min: 2,
            max: 5,
            defaultValue: 3,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="definition-matching-page">
        <div className="definition-matching-results">
          <GameResults
            title="Resultados"
            correctAnswers={correctAnswers}
            totalAnswers={totalAnswers}
            accuracy={accuracy}
            errors={totalErrors}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="definition-matching-results-grid">
            <div className="definition-matching-result-card">
              <span>Aciertos</span>
              <strong>{correctAnswers}</strong>
            </div>

            <div className="definition-matching-result-card">
              <span>Errores</span>
              <strong>{errors}</strong>
            </div>

            <div className="definition-matching-result-card">
              <span>Precisión</span>
              <strong>
                {Math.round(accuracy)}%
              </strong>
            </div>

            <div className="definition-matching-result-card">
              <span>Definiciones realizadas</span>
              <strong>{totalAnswers}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="definition-matching-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="definition-matching-page">
      <main className="definition-matching-card">
        <header className="definition-matching-header">
          <div>
            <p className="definition-matching-area">
              Comprensión lectora y vocabulario
            </p>

            <h1>Definiciones</h1>

            <p>
              Selecciona la palabra que mejor se
              ajusta a la definición.
            </p>
          </div>

          <div className="definition-matching-score">
            <span>Aciertos</span>
            <strong>{correctAnswers}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="definition-matching-game">
          {config?.showCategory && (
            <span className="definition-matching-category">
              {round?.category}
            </span>
          )}

          <div className="definition-matching-definition">
            <span>Definición</span>

            <p>{round?.definition}</p>
          </div>

          <p className="definition-matching-question">
            ¿Qué palabra corresponde con esta
            definición?
          </p>

          <div
            className="definition-matching-options"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                round?.options.length || 2,
                3
              )}, minmax(150px, 1fr))`,
            }}
          >
            {round?.options.map((option) => {
              const isSelected =
                selectedOptionId === option.id;

              const className = [
                "definition-matching-option",

                isSelected &&
                  option.isCorrect &&
                  "definition-matching-option-correct",

                isSelected &&
                  !option.isCorrect &&
                  "definition-matching-option-error",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  type="button"
                  key={option.id}
                  className={className}
                  onClick={() =>
                    handleAnswer(option)
                  }
                  disabled={responseLocked}
                >
                  {option.text}
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

export default DefinitionMatching;